import { buildGraphqlQuery } from '@ztechapps/common.utils'
import { gql } from '@apollo/client'
import { assetFragment, taskStatusActionConfigFragment } from '@ztechapps/task.common'

export const workOrderRemarksActivityFragment = gql`
  fragment workOrderRemarksActivityFragment on Activity {
    _id
    activity
    by {
      _id
      profile {
        _id
        name
        picture
      }
    }
    updatedAt
    isDeleted
  }
`

export const workOrderTasksFragment = gql`
  fragment workOrderTasksFragment on Task {
    _id
    name
    code
    remarks
    status
    startDate
    endDate
    timeSpent
    laborCost
    estimatedDuration
    perUnitCost
    total
    tags
    description
    project {
      _id
      code
      status
      timeSpent
    }
    workOrder {
      _id
    }
    members {
      _id
      profile {
        _id
        name
        picture
        email
      }
    }

    remarksActivity {
      ...workOrderRemarksActivityFragment
    }
    statusActions {
      ...taskStatusActionConfigFragment
    }
    assets {
      ...assetFragment
    }
  }
  ${taskStatusActionConfigFragment}
  ${assetFragment}
`

export const workOrderInsufficientItemsFragment = gql`
  fragment workOrderInsufficientItemsFragment on WorkOrderInsufficientItem {
    _id
    product {
      _id
      code
      name
      unitOfMeasurePrecision
      workOrderUom {
        _id
        symbol
      }
      baseUom {
        _id
        symbol
      }
    }
    qtyNeededToBuild
    cost
    decimalPrecision
  }
`

export const workOrderDocumentFragment = gql`
  fragment workOrderDocumentFragment on WorkOrder {
    _id
    tags
    createdAt
    updatedAt
    status
    completionDate
    code
    quantity
    product {
      _id
      name
      code
      coverImageThumb
      hasSubComponents
      requiredSerialNumber
      serialNumberType
      requiredComputedQuantityOnHand
      inventory {
        _id
        quantityOnHand
      }
      baseUom {
        _id
        code
        symbol
      }
      unitOfMeasurePrecision
      workOrderUom {
        _id
        code
        symbol
      }
      workOrderConversionFactor
    }
    projects {
      _id
      code
      name
      status
    }
    relatedWorkOrders {
      _id
      code
      status
    }
    relatedSalesOrders {
      _id
      code
      status
      summary
      paymentStatus
    }
    prioritized
    inventoryAdjustedByTaskAction
    miscellaneousCost
    totalOperationsCost
    subAssemblyCost
    totalManufactureCost
    insufficientItems {
      ...workOrderInsufficientItemsFragment
    }
    inventoryBySerialNumbers {
      _id
      code
      status
      remarks
      tags
    }

    remarksActivity {
      ...workOrderRemarksActivityFragment
    }
    tasks {
      ...workOrderTasksFragment
    }

    items {
      _id
      product {
        _id
        code
        name
        coverImage
        coverImageThumb
        minimumQuantityAllowance
        hasSubComponents
        inventory {
          _id
          quantityOnHand
        }
        requiredComputedQuantityOnHand
        baseUom {
          _id
          code
          symbol
        }
        unitOfMeasurePrecision
        workOrderUom {
          _id
          code
          symbol
        }
        workOrderConversionFactor
        billOfMaterials {
          _id
          product {
            _id
          }
          quantity
          requiredToBuild
        }
        requiredSerialNumber
        serialNumberType
      }
      quantity
      uom {
        _id
        code
        name
        symbol
      }
      remarks
      status
      inventoryBySerialNumbers {
        _id
        code
        status
        transactionType
        transactionRef
        product {
          _id
          code
        }
      }
      workOrder {
        _id
        code
        status
        progressNow
        progressMax
        totalManufactureCost
      }
      tasks {
        ...workOrderTasksFragment
      }
    }
    scrappedItems {
      _id
      scrappedQuantity
      product {
        _id
        code
        name
      }
      uom {
        _id
        symbol
      }
      inventoryBySerialNumbers {
        _id
        code
      }
    }
    progressNow
    progressMax
    assets {
      _id
      title
      subtitle
      url
      type
    }
    relatedLogistics {
      _id
      code
      status
    }
    relatedPurchaseOrders {
      _id
      code
      status
    }
  }
  ${workOrderRemarksActivityFragment}
  ${workOrderTasksFragment}
  ${workOrderInsufficientItemsFragment}
`

export const workOrderListFragment = gql`
  fragment workOrderListFragment on WorkOrder {
    _id
    tags
    remarks
    createdAt
    updatedAt
    status
    completionDate
    code
    quantity
    hasParent
    hasChildren
    product {
      _id
      name
      code
      workOrderUom {
        _id
        code
        symbol
      }
      unitOfMeasurePrecision
    }
    progressNow
    progressMax
    remarksActivity(filter: {}, options: { sort: { updatedAt: -1 }, limit: 10 }) {
      ...workOrderRemarksActivityFragment
    }
  }
  ${workOrderRemarksActivityFragment}
`

export const workOrderChildrenListQuery = gql`
  query workOrderChildrenList($filter: JSON!) {
    workOrderChildrenList(filter: $filter) {
      ...workOrderListFragment
    }
  }
  ${workOrderListFragment}
`

export const workOrderDocumentQueries = buildGraphqlQuery('WorkOrder', workOrderDocumentFragment, [
  'items',
  'relatedWorkOrders',
  'relatedSalesOrders',
  'relatedPurchaseOrders',
  'remarksActivity',
  'insufficientItems',
])
